/**
 * Shared pieces of the Day Ahead and Complete Backtesting tabs: formatters, the day-ahead policy
 * defaults, the configuration controls, and the report cards (summary tiles, volume
 * path, deferrals, breakdowns, SK accuracy, cumulative chart, daily list with the hour / quarter
 * drill-down). Both tabs render their day-ahead leg with the same components, so they read alike.
 */
import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, GitBranch, Activity, Target } from "lucide-react";

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------
export const fmtInt = (v) =>
  v == null || v === "" || Number.isNaN(Number(v)) ? "–" : Math.round(Number(v)).toLocaleString("en-US");

export const fmtNum = (v, dec = 2) =>
  v == null || v === "" || Number.isNaN(Number(v))
    ? "–"
    : Number(v).toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });

export const fmtPct = (v, dec = 1) => (v == null || v === "" ? "–" : `${fmtNum(v, dec)}%`);

export const fmtSigned = (v) => {
  if (v == null || v === "" || Number.isNaN(Number(v))) return "–";
  const n = Math.round(Number(v));
  return `${n > 0 ? "+" : ""}${n.toLocaleString("en-US")}`;
};

export const signColor = (v, dark) => {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return dark ? "text-slate-300" : "text-slate-600";
  return n > 0 ? (dark ? "text-emerald-400" : "text-emerald-600") : dark ? "text-rose-400" : "text-rose-600";
};

/** Class names shared by both tabs, light or dark. */
export const tones = (dark) => {
  const muted = dark ? "text-slate-400" : "text-slate-500";
  return {
    muted,
    faint: dark ? "text-slate-500" : "text-slate-400",
    rowBorder: dark ? "border-slate-800" : "border-slate-100",
    headCls: `${muted} text-xs uppercase tracking-wide`,
    bodyCls: dark ? "text-slate-200" : "text-slate-700",
    labelCls: `block text-xs font-medium mb-1 whitespace-nowrap truncate ${muted}`,
    inputCls: `w-full h-10 rounded-lg border px-3 py-2 text-sm ${
      dark ? "bg-slate-800 border-slate-700 text-slate-100 focus:border-blue-500" : "bg-white border-slate-300 text-slate-800 focus:border-blue-500"
    } focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50`,
    smallInputCls: `w-24 rounded-md border px-2 py-1 text-sm text-right ${
      dark ? "bg-slate-800 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800"
    } focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-40`,
    stageCls: `rounded-xl border p-3 space-y-2 ${dark ? "bg-slate-800/40 border-slate-700" : "bg-slate-50 border-slate-200"}`,
    stageTitle: `text-xs font-semibold uppercase tracking-wide ${dark ? "text-slate-300" : "text-slate-600"}`,
    btnSecondary: `inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
      dark ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-600 hover:bg-slate-50"
    }`,
  };
};

/** First day both backtesting tabs open on (owner, 2026-11-02). */
export const DEFAULT_START = "2025-12-01";

/** Tomorrow (browser date), ISO. */
export const tomorrow = () => {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
};

/** Default dates: DEFAULT_START (or the first data day, if later) → tomorrow. An end past the data is
 * fine: the backend stops at the last settled day and the results show the window actually used. */
export const defaultDates = (first) => ({ start: first && first > DEFAULT_START ? first : DEFAULT_START, end: tomorrow() });

export const TIME_LABEL = {
  1140: "11:40 D-1", 1440: "14:40 D-1", 1530: "15:30 D-1", 1630: "16:30 D-1", 1800: "18:00 D-1",
  2000: "20:00 D-1", 2140: "21:40 D-1", "0940": "09:40 D",
};
// SK forecast model: v50-k only (owner, 2026-11-02 - the other models are no longer used).
export const MODEL_LABEL = { v50k: "v50-k" };

// ---------------------------------------------------------------------------
// Day-ahead policy: defaults (mirror backend engine.DEFAULTS)
// ---------------------------------------------------------------------------
export const DA_DEFAULT_PARAMS = {
  model: "v50k",
  cap_fix2_mw: 50, cap_ida1_mw: 50,
  f2_sell_on: false, f2_sell_thr: -300,
  f2_buy_on: false, f2_buy_thr: 500,
  // owner defaults 2026-11-02: keep shorts past IDA1 when SK <= +300, to balancing when SK <= -100
  i1_sell_on: true, i1_sell_thr: 300,
  i1_buy_on: false, i1_buy_thr: 200,
  i2_time: "2140",
  i2_sell_on: true, i2_sell_thr: -100,
  i2_buy_on: false, i2_buy_thr: 400,
  i2_sell_q90_guard: null, i2_buy_q10_guard: null,
  i3_sell_on: false, i3_sell_thr: 0,
  i3_buy_on: false, i3_buy_thr: 0,
  transaction_fee_rate: 0.05, volume_fee_rate: 0.45,
};

/** Day-ahead parameters as query-string pairs (empty cap = "none", empty guard = omitted). */
export const daQueryPairs = (params) => {
  const out = [];
  Object.entries(params).forEach(([k, v]) => {
    if (k === "cap_fix2_mw" || k === "cap_ida1_mw" || k === "pos_limit_mw") out.push([k, v === "" || v == null ? "none" : String(v)]);
    else if (v !== "" && v != null) out.push([k, String(v)]);
  });
  return out;
};

// ---------------------------------------------------------------------------
// Small presentational pieces
// ---------------------------------------------------------------------------
export const KpiCard = ({ label, value, sub, accent, dark }) => (
  <div className={`rounded-xl border p-4 ${dark ? "bg-slate-800/60 border-slate-700" : "bg-white border-slate-200"}`}>
    <div className={`text-xs font-medium ${dark ? "text-slate-400" : "text-slate-500"}`}>{label}</div>
    <div className={`mt-1 text-2xl font-bold ${accent || (dark ? "text-slate-100" : "text-slate-800")}`}>{value}</div>
    {sub ? <div className={`mt-0.5 text-xs ${dark ? "text-slate-500" : "text-slate-400"}`}>{sub}</div> : null}
  </div>
);

export const SectionCard = ({ title, icon, children, dark, right }) => (
  <div className={`rounded-2xl border p-5 ${dark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200 shadow-sm"}`}>
    <div className="flex items-center justify-between mb-4">
      <h2 className={`flex items-center gap-2 text-sm font-semibold uppercase tracking-wide ${dark ? "text-slate-300" : "text-slate-600"}`}>
        {icon}
        {title}
      </h2>
      {right}
    </div>
    {children}
  </div>
);

export const Bars = ({ rows, valueKey, labelKey, dark, fmt = fmtInt, unit = "" }) => {
  const max = Math.max(1, ...rows.map((r) => Math.abs(Number(r[valueKey]) || 0)));
  return (
    <div className="space-y-1.5">
      {rows.map((r, i) => {
        const v = Number(r[valueKey]) || 0;
        return (
          <div key={i} className="flex items-center gap-2 text-xs">
            <div className={`w-20 shrink-0 truncate ${dark ? "text-slate-400" : "text-slate-500"}`}>{r[labelKey]}</div>
            <div className={`flex-1 h-4 rounded ${dark ? "bg-slate-800" : "bg-slate-100"}`}>
              <div className={`h-4 rounded ${v >= 0 ? "bg-emerald-500/80" : "bg-rose-500/80"}`} style={{ width: `${(Math.abs(v) / max) * 100}%` }} />
            </div>
            <div className={`w-28 shrink-0 text-right font-medium ${signColor(v, dark)}`}>
              {fmt(v)}
              {unit}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Two-series cumulative line chart (no charting dependency): rows carry cum_goose and cum_total.
export const CumChart = ({ rows, dark }) => {
  const W = 800;
  const H = 180;
  if (!rows.length) return null;
  const all = rows.flatMap((r) => [Number(r.cum_goose) || 0, Number(r.cum_total) || 0]);
  const lo = Math.min(0, ...all);
  const hi = Math.max(1, ...all);
  const x = (i) => (rows.length === 1 ? W / 2 : (i / (rows.length - 1)) * W);
  const y = (v) => H - ((v - lo) / (hi - lo)) * H;
  const path = (key) => rows.map((r, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(Number(r[key]) || 0).toFixed(1)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-44" role="img" aria-label="Cumulative P&L">
      <line x1="0" x2={W} y1={y(0)} y2={y(0)} stroke={dark ? "#334155" : "#cbd5e1"} strokeWidth="1" />
      <path d={path("cum_goose")} fill="none" stroke={dark ? "#94a3b8" : "#64748b"} strokeWidth="2" vectorEffect="non-scaling-stroke" />
      <path d={path("cum_total")} fill="none" stroke="#10b981" strokeWidth="2" vectorEffect="non-scaling-stroke" />
    </svg>
  );
};

export const Th = ({ children, left }) => (
  <th className={`${left ? "text-left" : "text-right"} font-medium py-2 px-2 whitespace-nowrap`}>{children}</th>
);

// One stage row: enable checkbox + signed SK threshold. Module level so inputs keep focus while typing.
export const StageRow = ({ side, onKey, thrKey, text, params, setParam, inputCls, muted, faint }) => (
  <label className="flex items-center gap-2 text-xs">
    <input type="checkbox" checked={!!params[onKey]} onChange={(e) => setParam(onKey, e.target.checked)} />
    <span className={`font-semibold w-10 ${side === "SELL" ? "text-rose-600" : "text-emerald-600"}`}>{side}</span>
    <span className={`flex-1 ${muted}`}>{text}</span>
    <input
      type="number"
      step="50"
      aria-label={thrKey}
      className={inputCls}
      value={params[thrKey] ?? ""}
      disabled={!params[onKey]}
      onChange={(e) => setParam(thrKey, e.target.value === "" ? "" : Number(e.target.value))}
    />
    <span className={faint}>MW</span>
  </label>
);

// Optional quantile guard: null = off.
export const GuardRow = ({ gKey, text, dflt, params, setParam, inputCls, muted, faint }) => {
  const on = params[gKey] !== null && params[gKey] !== undefined;
  return (
    <label className="flex items-center gap-2 text-xs">
      <input type="checkbox" checked={on} onChange={(e) => setParam(gKey, e.target.checked ? dflt : null)} />
      <span className={`font-semibold w-10 ${faint}`}>guard</span>
      <span className={`flex-1 ${muted}`}>{text}</span>
      <input
        type="number"
        step="50"
        aria-label={gKey}
        className={inputCls}
        value={on ? params[gKey] : ""}
        disabled={!on}
        onChange={(e) => setParam(gKey, e.target.value === "" ? "" : Number(e.target.value))}
      />
      <span className={faint}>MW</span>
    </label>
  );
};

// ---------------------------------------------------------------------------
// Configuration blocks
// ---------------------------------------------------------------------------
/** The four stage cards of the day-ahead carry policy. */
export const PolicyControls = ({ params, setParam, ida2Times, dark }) => {
  const t = tones(dark);
  const rowProps = { params, setParam, inputCls: t.smallInputCls, muted: t.muted, faint: t.faint };
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <div className={t.stageCls}>
          <div className={t.stageTitle}>1 · Fixing II stage — decision 11:40 D-1</div>
          <div className={`text-[11px] ${t.faint}`}>Do not close at Fixing II; keep the position for IDA1 and later.</div>
          <StageRow {...rowProps} side="SELL" onKey="f2_sell_on" thrKey="f2_sell_thr" text="keep shorts open when SK forecast ≤" />
          <StageRow {...rowProps} side="BUY" onKey="f2_buy_on" thrKey="f2_buy_thr" text="keep longs open when SK forecast ≥" />
        </div>
        <div className={t.stageCls}>
          <div className={t.stageTitle}>2 · IDA1 stage — decision 14:40 D-1</div>
          <div className={`text-[11px] ${t.faint}`}>Do not close at IDA1; defer the exit to the IDA2 decision.</div>
          <StageRow {...rowProps} side="SELL" onKey="i1_sell_on" thrKey="i1_sell_thr" text="keep shorts open when SK forecast ≤" />
          <StageRow {...rowProps} side="BUY" onKey="i1_buy_on" thrKey="i1_buy_thr" text="keep longs open when SK forecast ≥" />
        </div>
        <div className={t.stageCls}>
          <div className="flex items-center justify-between gap-2">
            <div className={t.stageTitle}>3 · IDA2 stage — decision</div>
            <select
              aria-label="IDA2 decision time"
              className={`rounded-md border px-2 py-1 text-xs ${dark ? "bg-slate-800 border-slate-700 text-slate-100" : "bg-white border-slate-300 text-slate-800"}`}
              value={params.i2_time}
              onChange={(e) => setParam("i2_time", e.target.value)}
            >
              {(ida2Times || ["2140"]).map((tm) => (
                <option key={tm} value={tm}>{TIME_LABEL[tm] || tm}</option>
              ))}
            </select>
          </div>
          <div className={`text-[11px] ${t.faint}`}>Deferred volume settles on the balancing market when the forecast agrees, otherwise it is closed at IDA2.</div>
          <StageRow {...rowProps} side="SELL" onKey="i2_sell_on" thrKey="i2_sell_thr" text="shorts to balancing when SK forecast ≤" />
          <StageRow {...rowProps} side="BUY" onKey="i2_buy_on" thrKey="i2_buy_thr" text="longs to balancing when SK forecast ≥" />
          <GuardRow {...rowProps} gKey="i2_sell_q90_guard" dflt={800} text="close shorts at IDA2 if v50-k q90 ≥" />
          <GuardRow {...rowProps} gKey="i2_buy_q10_guard" dflt={-600} text="close longs at IDA2 if v50-k q10 ≤" />
        </div>
        <div className={t.stageCls}>
          <div className={t.stageTitle}>4 · IDA3 stage — decision 09:40 D (delivery 12:00–24:00 only)</div>
          <div className={`text-[11px] ${t.faint}`}>Stop for volume on its way to the balancing market; earlier hours cannot be closed at IDA3.</div>
          <StageRow {...rowProps} side="SELL" onKey="i3_sell_on" thrKey="i3_sell_thr" text="close shorts at IDA3 when morning forecast ≥" />
          <StageRow {...rowProps} side="BUY" onKey="i3_buy_on" thrKey="i3_buy_thr" text="close longs at IDA3 when morning forecast ≤" />
        </div>
      </div>
    </>
  );
};

/** Transaction + volume fee inputs: charged the same way at every venue, the balancing market included. */
export const FeeControls = ({ params, setParam, dark }) => {
  const t = tones(dark);
  const num = (e) => (e.target.value === "" ? "" : Number(e.target.value));
  return (
    <>
      <div>
        <label className={t.labelCls} title="Percent of sell-side revenue on every trade or settlement that sells: BASE, Fixing I/II, IDA1/2/3, intraday and the balancing market.">
          Transaction fee (% of sales)
        </label>
        <input type="number" step="0.01" min="0" className={t.inputCls} value={params.transaction_fee_rate} onChange={(e) => setParam("transaction_fee_rate", num(e))} />
      </div>
      <div>
        <label className={t.labelCls} title="PLN per MWh on every MWh traded or settled, at every venue: BASE, Fixing I/II, IDA1/2/3, intraday and the balancing market.">
          Volume fee (PLN/MWh)
        </label>
        <input type="number" step="0.05" min="0" className={t.inputCls} value={params.volume_fee_rate} onChange={(e) => setParam("volume_fee_rate", num(e))} />
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Report cards for the day-ahead leg (the Day Ahead engine's report payload)
// ---------------------------------------------------------------------------
/** Three tiles in one row: Total, Day-ahead (MWh closed on the exchange: BASE, Fixing I/II, IDA1-3) and
 * Balancing market (MWh settled at CEN). Each block's P&L is entry -> exit of the MWh closed there, so
 * Day-ahead + Balancing market = Total; "Added vs Goose" is the same split of the policy's added P&L. */
export const SplitSummary = ({ split, gooseTotal, dark }) => {
  if (!split) return null;
  const t = tones(dark);
  const { total: T, exchange: X, balancing: B } = split;
  const pct = (v) => (v == null ? "–" : `${fmtNum(v, 1)}`);
  const share = T.mwh ? (Number(X.mwh) / Number(T.mwh)) * 100 : null;
  const money = (bl) => [
    ["P&L after fees (PLN)", fmtInt(bl.pnl_after_fees), signColor(bl.pnl_after_fees, dark)],
    ["Fees (PLN)", fmtInt(bl.fees)],
    ["Volume (MWh)", fmtInt(bl.mwh)],
    ["P&L per MWh (PLN)", fmtNum(bl.pln_per_mwh, 2), signColor(bl.pln_per_mwh, dark)],
    ["Added vs Goose (PLN)", fmtSigned(bl.added_pln), signColor(bl.added_pln, dark)],
  ];
  const tile = (title, note, bl, extra) => (
    <div className={`rounded-xl border p-4 ${dark ? "bg-slate-800/60 border-slate-700" : "bg-white border-slate-200"}`}>
      <div className={`text-xs font-semibold uppercase tracking-wide ${t.muted}`}>{title}</div>
      <div className={`text-[11px] ${t.faint}`}>{note}</div>
      <div className={`mt-2 text-xs ${t.muted}`}>P&L (PLN)</div>
      <div className={`text-2xl font-bold ${signColor(bl.pnl, dark) || (dark ? "text-slate-100" : "text-slate-800")}`}>{fmtInt(bl.pnl)}</div>
      <dl className="mt-3 space-y-1 text-xs">
        {[...money(bl), ...extra].map(([label, value, cls]) => (
          <div key={label} className={`flex justify-between gap-3 border-t pt-1 ${t.rowBorder}`}>
            <dt className={t.muted}>{label}</dt>
            <dd className={`font-semibold ${cls || t.bodyCls}`}>{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      {tile("Total", "day-ahead + balancing market", T, [["Goose alone (PLN)", fmtInt(gooseTotal)]])}
      {tile("Day-ahead", "closed on the exchange: BASE, Fixing I/II, IDA1–3", X, [["Share of the volume (%)", pct(share)]])}
      {tile("Balancing market", "settled at CEN", B, [
        ["Days with a BM position", `${fmtInt(B.days)} of ${fmtInt(B.days_total)}`],
        ["Avg per day with BM (MWh)", fmtNum(B.mwh_per_bm_day, 1)],
        ["Avg per day, all days (MWh)", fmtNum(B.mwh_per_day, 1)],
        [`Largest day (MWh, ${B.max_day || "–"})`, fmtInt(B.max_day_mwh)],
        ["Short / long (MWh)", `${fmtInt(B.short_mwh)} / ${fmtInt(B.long_mwh)}`],
        ["Directional accuracy (%)", pct(B.dir_acc)],
        ["Win rate vs Goose exit (%)", pct(B.win_pct)],
      ])}
    </div>
  );
};

/** MWh closed at each venue (Goose vs this policy) and what each move (cap close or deferral) earned.
 * BASE only opens; Fixing I closes part of BASE and opens the Fix I position, so closes start at Fixing I.
 * `extraCloses` (Complete tab: intraday positions) are listed under the total, outside the day-ahead book. */
export const VolumeAndDeferrals = ({ report, extraCloses = [], dark }) => {
  const t = tones(dark);
  const summary = report.summary;
  const vol = report.volume || {};
  const op = vol.opened || {};
  const notClosed = Number(vol.not_closed_mwh) || 0;
  const ncDays = vol.not_closed_days || [];
  const rows = [...(report.closes || [])];
  if (notClosed > 0.5) rows.push({ venue: "Not closed in the Goose result", goose_mwh: notClosed, policy_mwh: notClosed, warn: true });
  const sum = (k) => rows.reduce((acc, r) => acc + (Number(r[k]) || 0), 0);
  const gTot = sum("goose_mwh");
  const pTot = sum("policy_mwh");
  const moves = report.moves || [];
  const row = (r, extra) => {
    const g = Number(r.goose_mwh) || 0;
    const p = Number(r.policy_mwh) || 0;
    const share = !extra && pTot > 0 ? (p / pTot) * 100 : null;
    return (
      <tr key={r.venue} className={`border-t ${t.rowBorder}`}>
        <td className={`py-1.5 px-2 text-xs ${r.warn ? "text-amber-600" : t.muted}`}>{r.venue}</td>
        <td className="py-1.5 px-2 text-right">{fmtInt(g)}</td>
        <td className="py-1.5 px-2 text-right font-semibold">{fmtInt(p)}</td>
        <td className="py-1.5 px-2 text-right">{p - g ? fmtSigned(p - g) : "·"}</td>
        <td className="py-1.5 px-2 text-right">
          {share == null ? "" : (
            <div className="flex items-center justify-end gap-2">
              <div className={`h-1.5 w-12 rounded ${dark ? "bg-slate-800" : "bg-slate-100"}`}>
                <div className="h-1.5 rounded bg-blue-500" style={{ width: `${share}%` }} />
              </div>
              <span>{fmtNum(share, 1)}</span>
            </div>
          )}
        </td>
      </tr>
    );
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <SectionCard title="Volume closed by venue" icon={<GitBranch className="w-4 h-4" />} dark={dark}>
        <div className="overflow-x-auto">
          <table className="app-table w-full text-sm">
            <thead>
              <tr className={t.headCls}>
                <Th left>Closed at</Th>
                <Th>Goose (MWh)</Th>
                <Th>This policy (MWh)</Th>
                <Th>Difference (MWh)</Th>
                <Th>Share of policy (%)</Th>
              </tr>
            </thead>
            <tbody className={t.bodyCls}>
              {rows.map((r) => row(r, false))}
              <tr className={`border-t-2 ${t.rowBorder} font-semibold`}>
                <td className="py-1.5 px-2 text-xs">Total</td>
                <td className="py-1.5 px-2 text-right">{fmtInt(gTot)}</td>
                <td className="py-1.5 px-2 text-right">{fmtInt(pTot)}</td>
                <td className="py-1.5 px-2 text-right">{Math.round(pTot - gTot) ? fmtSigned(pTot - gTot) : "·"}</td>
                <td className="py-1.5 px-2 text-right">100.0</td>
              </tr>
              {extraCloses.map((r) => row(r, true))}
            </tbody>
          </table>
        </div>
        <div className={`mt-3 text-xs ${t.muted}`}>
          Opened (MWh): BASE <span className={`font-semibold ${t.bodyCls}`}>{fmtInt(op.base_mwh)}</span> · Fixing I position{" "}
          <span className={`font-semibold ${t.bodyCls}`}>{fmtInt(op.fix1_mwh)}</span> · Fixing II bucket{" "}
          <span className={`font-semibold ${t.bodyCls}`}>{fmtInt(op.fix2_bucket_mwh)}</span> = <span className={`font-semibold ${t.bodyCls}`}>{fmtInt(vol.opened_mwh)}</span>.
          BASE only opens; Fixing I closes part of BASE and opens the Fix I position.
        </div>
        {notClosed > 0.5 ? (
          <div className="mt-1 text-[11px] text-amber-600">
            {fmtInt(notClosed)} MWh on {ncDays.length} day(s) ({ncDays.slice(0, 4).join(", ")}{ncDays.length > 4 ? ", …" : ""}) are never closed, mostly
            because the data has no IDA1 price for the day ({fmtInt(vol.not_closed_days_no_ida1)} of {ncDays.length} days): Goose cannot close at IDA1 there,
            its result gives that volume no P&amp;L and the policy never sees it.
          </div>
        ) : null}
        <div className={`mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs ${t.muted}`}>
          <div>Closed at Fixing II by the cap: <span className={`font-semibold ${t.bodyCls}`}>{fmtInt(summary.capped_fix2_mwh)} MWh</span></div>
          <div>Kept open past Fixing II: <span className={`font-semibold ${t.bodyCls}`}>{fmtInt(summary.past_fix2_mwh)} MWh</span></div>
          <div>Kept open past IDA1: <span className={`font-semibold ${t.bodyCls}`}>{fmtInt(summary.past_ida1_mwh)} MWh</span></div>
          <div>Balancing days: <span className={`font-semibold ${t.bodyCls}`}>{fmtInt(summary.rb_days)} of {fmtInt(summary.days)}</span></div>
        </div>
      </SectionCard>

      <SectionCard title="What the moved volume earned" icon={<Target className="w-4 h-4" />} dark={dark}>
        <div className="overflow-x-auto">
          <table className="app-table w-full text-sm">
            <thead>
              <tr className={t.headCls}>
                <Th left>Volume moved</Th>
                <Th>MWh</Th>
                <Th>Added P&L (PLN)</Th>
                <Th>PLN / MWh</Th>
                <Th>Win %</Th>
              </tr>
            </thead>
            <tbody className={t.bodyCls}>
              {moves.map((m) => (
                <tr key={m.move} className={`border-t ${t.rowBorder}`}>
                  <td className={`py-2 px-2 text-xs ${t.muted}`}>{m.move}</td>
                  <td className="py-2 px-2 text-right">{fmtInt(m.mwh)}</td>
                  <td className={`py-2 px-2 text-right font-medium ${signColor(m.delta_pnl, dark)}`}>{fmtSigned(m.delta_pnl)}</td>
                  <td className={`py-2 px-2 text-right ${signColor(m.pln_per_mwh, dark)}`}>{fmtNum(m.pln_per_mwh, 1)}</td>
                  <td className="py-2 px-2 text-right">{fmtPct(m.win_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className={`mt-2 text-[11px] ${t.faint}`}>Win % = share of MWh whose new exit price beat the venue where Goose would have closed it.</div>
      </SectionCard>
    </div>
  );
};

/** Added P&L of the carry policy by side, product, Goose exit venue and delivery hours. */
export const BreakdownCard = ({ report, dark }) => {
  const t = tones(dark);
  return (
    <SectionCard title="Breakdown of the added P&L" icon={<Activity className="w-4 h-4" />} dark={dark}>
      <div className="overflow-x-auto">
        <table className="app-table w-full text-sm">
          <thead>
            <tr className={t.headCls}>
              <Th left>Group</Th>
              <Th>Goose exits (MWh)</Th>
              <Th>Deferred (MWh)</Th>
              <Th>Closed IDA2 (MWh)</Th>
              <Th>Closed IDA3 (MWh)</Th>
              <Th>Balancing (MWh)</Th>
              <Th>Added P&L (PLN)</Th>
              <Th>PLN / deferred MWh</Th>
              <Th>Dir. accuracy</Th>
              <Th>Balancing win %</Th>
            </tr>
          </thead>
          <tbody className={t.bodyCls}>
            {[
              ["By side", report.by_side],
              ["By product", report.by_leg],
              ["By Goose exit venue", report.by_stage],
              ["By delivery hours", report.by_block],
            ].map(([title, rows]) => (
              <React.Fragment key={title}>
                <tr className={`border-t-2 ${dark ? "border-slate-700" : "border-slate-200"}`}>
                  <td colSpan={10} className={`py-1.5 px-2 text-[11px] font-semibold uppercase tracking-wide ${t.faint}`}>{title}</td>
                </tr>
                {(rows || []).map((r) => (
                  <tr key={`${title}|${r.group}`} className={`border-t ${t.rowBorder}`}>
                    <td className={`py-1.5 px-2 text-xs ${t.muted}`}>{r.group}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(r.ledger_mwh)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(r.deferred_mwh)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(r.ida2_mwh)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(r.ida3_mwh)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtInt(r.rb_mwh)}</td>
                    <td className={`py-1.5 px-2 text-right font-medium ${signColor(r.delta_pnl, dark)}`}>{fmtSigned(r.delta_pnl)}</td>
                    <td className={`py-1.5 px-2 text-right ${signColor(r.pln_per_deferred_mwh, dark)}`}>{fmtNum(r.pln_per_deferred_mwh, 1)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtPct(r.dir_acc)}</td>
                    <td className="py-1.5 px-2 text-right">{fmtPct(r.rb_win_pct)}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </SectionCard>
  );
};

/** v50-k SK forecast skill over the selected dates: by delivery-hour block x decision time, and by hour. */
export const SkAccuracyCard = ({ report, model, dark }) => {
  const t = tones(dark);
  const skByHour = report.sk_by_hour || [];
  const skRef = report.sk_ref || {};
  const pivot = useMemo(() => {
    const times = [];
    const blocks = [];
    const map = {};
    (report.sk_by_block || []).forEach((r) => {
      if (!times.includes(r.time)) times.push(r.time);
      if (!blocks.includes(r.block)) blocks.push(r.block);
      map[`${r.block}|${r.time}`] = r;
    });
    return { times, blocks, map };
  }, [report.sk_by_block]);
  return (
    <SectionCard title={`SK forecast accuracy · by delivery hours (${MODEL_LABEL[model] || model})`} icon={<Target className="w-4 h-4" />} dark={dark}>
      <div className="overflow-x-auto">
        <table className="app-table w-full text-sm">
          <thead>
            <tr className={t.headCls}>
              <Th left>Hours</Th>
              {pivot.times.map((tm) => (
                <Th key={tm}>{TIME_LABEL[tm] || tm}</Th>
              ))}
            </tr>
          </thead>
          <tbody className={t.bodyCls}>
            {pivot.blocks.map((bl) => (
              <tr key={bl} className={`border-t ${t.rowBorder}`}>
                <td className={`py-1.5 px-2 text-xs ${t.muted}`}>{bl}</td>
                {pivot.times.map((tm) => {
                  const r = pivot.map[`${bl}|${tm}`];
                  return (
                    <td key={tm} className="py-1.5 px-2 text-right whitespace-nowrap" title={r ? `MAE ${fmtNum(r.mae, 0)} MW · SELL acc ${fmtPct(r.sell_acc)} (n ${fmtInt(r.n_sell)}) · BUY acc ${fmtPct(r.buy_acc)} (n ${fmtInt(r.n_buy)})` : ""}>
                      {r ? (
                        <>
                          <span className="font-medium">{fmtPct(r.sign_acc, 0)}</span>
                          <span className={`ml-1 text-[11px] ${t.faint}`}>{fmtNum(r.mae, 0)}</span>
                        </>
                      ) : (
                        "–"
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className={`mt-2 text-[11px] ${t.faint}`}>Each cell: sign accuracy, then MAE in MW. Hover for SELL / BUY accuracy.</div>
      {skByHour.length ? (
        <>
          <div className={`mt-4 mb-1 text-xs font-medium ${t.muted}`}>
            By delivery hour at {TIME_LABEL[skRef.hour_time] || skRef.hour_time} · sign accuracy (MAE below)
          </div>
          <div className="overflow-x-auto">
            <table className="app-table w-full text-[11px]">
              <tbody className={t.bodyCls}>
                <tr className={t.muted}>
                  {skByHour.map((r) => (
                    <td key={r.hour} className="py-1 px-1 text-center">{String(r.hour).padStart(2, "0")}</td>
                  ))}
                </tr>
                <tr className={`border-t ${t.rowBorder}`}>
                  {skByHour.map((r) => (
                    <td
                      key={r.hour}
                      className={`py-1 px-1 text-center font-medium ${Number(r.sign_acc) >= 70 ? "text-emerald-600" : Number(r.sign_acc) < 62 ? "text-rose-600" : ""}`}
                      title={`MAE ${fmtNum(r.mae, 0)} MW · SELL acc ${fmtPct(r.sell_acc)} (n ${fmtInt(r.n_sell)}) · BUY acc ${fmtPct(r.buy_acc)} (n ${fmtInt(r.n_buy)})`}
                    >
                      {fmtNum(r.sign_acc, 0)}
                    </td>
                  ))}
                </tr>
                <tr className={t.faint}>
                  {skByHour.map((r) => (
                    <td key={r.hour} className="py-1 px-1 text-center">{fmtNum(r.mae, 0)}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </SectionCard>
  );
};

// ---------------------------------------------------------------------------
// Daily list with the day-ahead drill-down (hours -> quarters)
// ---------------------------------------------------------------------------
/** Hour / quarter table of one day (/dayahead/day payload). */
export const DayDetail = ({ detail, day, i2Label, dark }) => {
  const t = tones(dark);
  const [openHours, setOpenHours] = useState({});
  if (detail === "loading" || detail === undefined) return <div className={`text-xs ${t.muted}`}>Loading the day…</div>;
  if (!detail.hours?.length) return <div className={`text-xs ${t.muted}`}>No data for this day.</div>;
  const hs = detail.hours || [];
  const fired = (k) => hs.filter((h) => h[k] != null && Number(h[k]) !== 0);
  const dn = fired("down_shift");
  const up = fired("up_shift");
  const avg = (a, k) => fmtNum(a.reduce((s, h) => s + Number(h[k]), 0) / a.length, 1);
  const sm = detail.summary || {};
  const lbl = detail.i2_label || i2Label || "";
  return (
    <>
      <div className={`flex flex-wrap gap-x-6 gap-y-1 mb-3 text-xs ${t.muted}`}>
        <span>Day-ahead: BASE <span className={`font-medium ${signColor(sm.da_base_pnl, dark)}`}>{fmtInt(sm.da_base_pnl)}</span></span>
        <span>Fix I <span className={`font-medium ${signColor(sm.da_fix1_pnl, dark)}`}>{fmtInt(sm.da_fix1_pnl)}</span></span>
        <span>Fix II bucket <span className={`font-medium ${signColor(sm.da_bucket_pnl, dark)}`}>{fmtInt(sm.da_bucket_pnl)}</span></span>
        <span>Policy <span className={`font-medium ${signColor(detail.summary.delta_pnl, dark)}`}>{fmtSigned(detail.summary.delta_pnl)}</span></span>
        <span className={t.faint} title="Quantile shifts Goose applied on this day (hours where the shift fired, and its average size in PLN/MWh).">
          Shifts: down {dn.length ? `${dn.length} h (${avg(dn, "down_shift")})` : "–"} · up {up.length ? `${up.length} h (+${avg(up, "up_shift")})` : "–"}
        </span>
        <span className={t.faint}>
          Opened MWh: BASE {fmtInt(sm.open_base_mwh)} · Fixing I {fmtInt(sm.open_fix1_mwh)} · Fixing II bucket {fmtInt(sm.open_bucket_mwh)}
        </span>
        <span className={t.faint}>
          Closed MWh: Fixing I {fmtInt(sm.close_fix1_mwh)} · Fixing II {fmtInt(sm.close_fix2_mwh)} · IDA1 {fmtInt(sm.close_ida1_mwh)} · IDA2{" "}
          {fmtInt(sm.close_ida2_mwh)} · IDA3 {fmtInt(sm.close_ida3_mwh)} · balancing {fmtInt(sm.close_rb_mwh)}
          {Number(sm.not_closed_mwh) > 0.5 ? <span className="text-amber-600"> · not closed {fmtInt(sm.not_closed_mwh)} (no IDA1 price)</span> : null}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="app-table w-full text-xs whitespace-nowrap">
          <thead>
            <tr className={`${t.muted} uppercase tracking-wide text-[10px]`}>
              <th className="text-left py-1 px-2" rowSpan={2}>Hour</th>
              <th className="text-center py-1 px-2 border-l border-slate-300/40" colSpan={6}>Prices (PLN/MWh)</th>
              <th className="text-center py-1 px-2 border-l border-slate-300/40" colSpan={5}>SK (MW)</th>
              <th className="text-center py-1 px-2 border-l border-slate-300/40" colSpan={7}>Trade at each venue (MW, + buy / − sell)</th>
              <th className="text-center py-1 px-2 border-l border-slate-300/40" colSpan={6}>P&L (PLN)</th>
            </tr>
            <tr className={`${t.muted} uppercase tracking-wide text-[10px]`}>
              {["Fix I", "Fix II", "IDA1", "IDA2", "IDA3", "CEN"].map((c, i) => (
                <th key={c} className={`text-right py-1 px-2 ${i === 0 ? "border-l border-slate-300/40" : ""}`}>{c}</th>
              ))}
              {["11:40", "14:40", lbl.replace(" D-1", ""), "09:40 D", "settled"].map((c, i) => (
                <th key={`s${i}`} className={`text-right py-1 px-2 ${i === 0 ? "border-l border-slate-300/40" : ""}`}>{c}</th>
              ))}
              {["BASE", "Fix I", "Fix II", "IDA1", "IDA2", "IDA3", "BM"].map((c, i) => (
                <th key={`p${c}`} className={`text-right py-1 px-2 ${i === 0 ? "border-l border-slate-300/40" : ""}`}>{c}</th>
              ))}
              {["Day-ahead", "BASE", "Fix I", "Fix II bucket", "Added", "Combined"].map((c, i) => (
                <th key={`m${c}`} className={`text-right py-1 px-2 ${i === 0 ? "border-l border-slate-300/40" : ""} ${i >= 1 && i <= 3 ? "normal-case font-normal" : ""}`}
                  title={i >= 1 && i <= 3 ? "Part of Day-ahead, by where the position was opened." : undefined}>{c}</th>
              ))}
            </tr>
          </thead>
          <tbody className={t.bodyCls}>
            {hs.map((h) => {
              // `ph` is the hour's position in the day: on a DST fall-back day 02:00 is delivered
              // twice, so keying on the wall-clock hour would expand both rows together.
              const hk = `${day}|${h.ph ?? h.hour}`;
              const hOpen = !!openHours[hk];
              const cells = (r, isQ) => {
                const skOpp = r.f_i2 != null && r.sk_settled != null && Number(r.f_i2) * Number(r.sk_settled) < 0;
                const n = (v, dec = 0) => (v == null ? "–" : fmtNum(v, dec));
                const mw = (v) => (v == null || Number(v) === 0 ? "·" : fmtNum(v, 1));
                return (
                  <>
                    {["fix1", "fix2", "ida1", "ida2", "ida3", "cen"].map((c, i) => (
                      <td key={c} className={`py-1 px-2 text-right ${i === 0 ? "border-l border-slate-300/40" : ""} ${c === "cen" ? "font-medium" : ""}`}>{n(r[c], 0)}</td>
                    ))}
                    {["f_1140", "f_1440", "f_i2", "f_0940"].map((c, i) => (
                      <td key={c} className={`py-1 px-2 text-right ${i === 0 ? "border-l border-slate-300/40" : ""} ${signColor(r[c], dark)}`}>{n(r[c], 0)}</td>
                    ))}
                    <td className={`py-1 px-2 text-right font-medium ${skOpp ? "text-rose-600" : ""}`}>{n(r.sk_settled, 0)}</td>
                    {["base", "fix1", "fix2", "ida1", "ida2", "ida3", "bm"].map((c, i) => {
                      // BM = settling what is left at CEN (blue); amber = volume left open with no close at all
                      // (no IDA1 price that day: Goose has no close and no P&L for it)
                      const v = r[`trade_${c}_mw`];
                      const open = c === "bm" && Math.abs(Number(r.trade_open_mw) || 0) > 0.05;
                      const bm = c === "bm" && !open && Math.abs(Number(v) || 0) > 0.05;
                      return (
                        <td key={c}
                          className={`py-1 px-2 text-right ${i === 0 ? "border-l border-slate-300/40" : ""} ${open ? "font-semibold text-amber-600" : bm ? "font-semibold text-blue-600" : signColor(v, dark)}`}
                          title={open ? `${fmtNum(r.trade_open_mw, 1)} MW left open: no IDA1 price that day, so Goose has no close and no P&L for it.` : bm ? "Settled on the balancing market at CEN." : undefined}>
                          {open ? `open ${fmtNum(r.trade_open_mw, 1)}` : mw(v)}
                        </td>
                      );
                    })}
                    <td className={`py-1 px-2 text-right border-l border-slate-300/40 ${signColor(isQ ? r.goose_exit_pnl : r.goose_pnl, dark)}`}>{fmtInt(isQ ? r.goose_exit_pnl : r.goose_pnl)}</td>
                    {["da_base_pnl", "da_fix1_pnl", "da_bucket_pnl"].map((c) => (
                      <td key={c} className={`py-1 px-2 text-right text-[11px] opacity-80 ${signColor(r[c], dark)}`}>{Number(r[c]) ? fmtInt(r[c]) : "·"}</td>
                    ))}
                    <td className={`py-1 px-2 text-right ${signColor(r.delta_pnl, dark)}`}>{Number(r.delta_pnl) ? fmtSigned(r.delta_pnl) : "·"}</td>
                    <td className={`py-1 px-2 text-right font-semibold ${signColor(isQ ? r.exit_pnl : r.total_pnl, dark)}`}>{fmtInt(isQ ? r.exit_pnl : r.total_pnl)}</td>
                  </>
                );
              };
              return (
                <React.Fragment key={hk}>
                  <tr className={`border-t ${t.rowBorder} cursor-pointer ${dark ? "hover:bg-slate-800/50" : "hover:bg-slate-50"}`} onClick={() => setOpenHours((o) => ({ ...o, [hk]: !o[hk] }))}>
                    <td className="py-1 px-2 text-left font-medium">
                      {hOpen ? "▾" : "▸"} {h.label}
                      {h.fix1_action ? (
                        <span className={`ml-2 text-[10px] ${h.fix1_action === "buy" ? "text-emerald-600" : "text-rose-600"}`}>
                          Fix I {h.fix1_action} {fmtNum(h.fix1_mw, 0)}
                        </span>
                      ) : null}
                    </td>
                    {cells(h, false)}
                  </tr>
                  {hOpen
                    ? h.quarters.map((q) => (
                        <tr key={`${hk}|${q.qh}`} className={`${dark ? "bg-slate-800/30" : "bg-slate-50/70"} text-[11px]`}>
                          <td className={`py-1 px-2 pl-6 text-left ${t.faint}`}>{q.label}</td>
                          {cells(q, true)}
                        </tr>
                      ))
                    : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={`mt-2 text-[11px] ${t.faint}`}>
        Click an hour for its four quarters. Trade = MW bought (+) or sold (−) at each venue, read left to right: BASE opens the position;
        Fixing I buys or sells part of it back and can open a new one; Fixing II closes part and can open a 15-minute bucket; IDA1–3 close the
        rest; BM is the balancing-market settlement of what is left (blue). A row adds up to 0 — except amber: volume left open with no close
        (no IDA1 price that day). Hour rows show the hour&apos;s average MW (= its MWh). Day-ahead = Goose&apos;s P&amp;L,
        split by where the position was opened: BASE (incl. what Fixing I bought or sold back of it), the Fix I position and the Fix II bucket;
        Added = what the policy changes; Combined = both. Quarter rows add up to the hour (BASE bought or sold back at Fixing I is spread
        evenly over the four quarters). SK forecast in green = shortage (BUY side), red = surplus (SELL side); settled SK in red when it contradicts the {lbl} forecast.
      </div>
    </>
  );
};

/**
 * Expandable list of days. `stats(row)` renders the right-hand figures of a day's header line,
 * `fetchDay(day)` loads its drill-down (resolves to the /dayahead/day payload), `extra(row)` adds
 * content above the hour table (e.g. the intraday trades of the day). Collapses when `rows` change.
 */
export const DailyList = ({ rows, fetchDay, stats, extra, i2Label, dark }) => {
  const [openDay, setOpenDay] = useState(null);
  const [details, setDetails] = useState({});
  useEffect(() => {
    setOpenDay(null);
    setDetails({});
  }, [rows]);
  const toggle = async (day) => {
    if (openDay === day) {
      setOpenDay(null);
      return;
    }
    setOpenDay(day);
    if (!details[day]) {
      setDetails((d) => ({ ...d, [day]: "loading" }));
      let detail = { hours: [] };
      try {
        detail = (await fetchDay(day)) || { hours: [] };
      } catch (e) {
        detail = { hours: [] };
      }
      setDetails((d) => ({ ...d, [day]: detail }));
    }
  };
  return (
    <div className="space-y-1.5">
      {rows.map((d) => {
        const isOpen = openDay === d.day;
        const pos = Number(d.total_pnl) >= 0;
        const rowBg = pos ? (dark ? "bg-emerald-950/20" : "bg-emerald-50/60") : dark ? "bg-rose-950/20" : "bg-rose-50/60";
        return (
          <div key={d.day} className={`rounded-lg border overflow-hidden ${dark ? "border-slate-800" : "border-slate-200"}`}>
            <button onClick={() => toggle(d.day)} className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm ${rowBg}`}>
              {isOpen ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
              <span className={`font-semibold w-24 text-left ${dark ? "text-slate-200" : "text-slate-700"}`}>{d.day}</span>
              <span className={`hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${dark ? "bg-slate-800 text-slate-300" : "bg-slate-200 text-slate-600"}`}>
                BASE {d.base_action === "buy" || d.base_action === "sell" ? `${d.base_action} @ ${fmtNum(d.base_price, 2)}` : "idle"}
              </span>
              {Number(d.pending_quarters) > 0 ? <span className="text-[11px] text-amber-600">awaiting CEN</span> : null}
              <span className={`ml-auto flex flex-wrap items-center justify-end gap-x-5 gap-y-0.5 text-xs ${dark ? "text-slate-400" : "text-slate-500"}`}>
                {stats(d)}
              </span>
            </button>
            {isOpen ? (
              <div className={`border-t px-3 py-3 ${dark ? "border-slate-800" : "border-slate-200"}`}>
                {extra ? extra(d) : null}
                <DayDetail detail={details[d.day]} day={d.day} i2Label={i2Label} dark={dark} />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};
